import { debounce } from '../../utils/lodash-replacements';

export const SETTLE_MS = 120;
const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * How many slides fit in one viewport width.
 *
 * Consumers that showed several Flickity cells at once need this before
 * constructing the carousel, because `slidesPerView` decides both how many
 * clones the loop needs and what `groupCells: true` should map to. Measured
 * rather than derived from CSS, since slide widths are set per breakpoint (and
 * sometimes per section) in SCSS.
 *
 * Additive helper — existing consumers that pass an explicit `slidesPerView`
 * are unaffected.
 */
export const measureSlidesPerView = (viewport, slide) => {
  if (!viewport || !slide) return 1;
  const slideWidth = slide.offsetWidth;
  const viewportWidth = viewport.clientWidth;
  if (!slideWidth || !viewportWidth) return 1;
  return Math.max(1, Math.round(viewportWidth / slideWidth));
};

export const CLASSES = {
  viewport: 'ScrollCarousel__viewport',
  track: 'ScrollCarousel__track',
  slide: 'ScrollCarousel__slide',
  arrow: 'ScrollCarousel__arrow',
  arrowPrev: 'ScrollCarousel__arrow--previous',
  arrowNext: 'ScrollCarousel__arrow--next',
  arrowDisabled: 'ScrollCarousel__arrow--disabled',
  dots: 'ScrollCarousel__dots',
  dot: 'ScrollCarousel__dot',
  dotActive: 'ScrollCarousel__dot--active',
  pause: 'ScrollCarousel__pause',
  // Kept as the legacy Flickity name: core/INF-elementIsVisible.js gates
  // background-video playback on `.is-selected`, and several SCSS files
  // still target it.
  selected: 'is-selected',
};

/**
 * Scroll-snap carousel with an optional clone-based seamless loop. Replaces
 * Flickity across the theme with no node_modules dependency.
 *
 * Consumers opt into features rather than inheriting a fixed contract, because
 * the call sites vary widely: the announcement bar is one full-width text slide
 * with autoplay, the hero carousel needs adaptive height and change events, and
 * the pods carousel shows several slides at once.
 *
 * Markup contract — a viewport element wrapping a track element whose children
 * are the slides. Consumers may supply their own class names via `selectors`.
 */
class ScrollCarousel {
  constructor(element, options = {}) {
    this.element = element;
    this.options = options;

    // Consumers keep their own BEM namespace so existing Liquid/SCSS keeps
    // working; only the behavior is shared.
    this.classes = { ...CLASSES, ...(options.classes || {}) };

    const selectors = options.selectors || {};
    this.viewport = selectors.viewport
      ? element.querySelector(selectors.viewport)
      : element.querySelector(`.${this.classes.viewport}`);
    this.track = selectors.track
      ? element.querySelector(selectors.track)
      : element.querySelector(`.${this.classes.track}`);

    if (!this.viewport || !this.track) {
      this.valid = false;
      return;
    }
    this.valid = true;

    this.realSlides = Array.from(this.track.children).filter((child) => !child.hasAttribute('data-clone'));
    this.realCount = this.realSlides.length;

    // Data attributes are always strings — the Liquid emits the literal
    // "false" when a merchant disables autoplay, which is truthy. Parse to a
    // number and require a positive interval.
    const autoPlayMs = Number(options.autoPlay);
    this.autoPlayDelay = Number.isFinite(autoPlayMs) && autoPlayMs > 0 ? autoPlayMs : 0;

    this.slidesPerView = Math.max(1, options.slidesPerView || 1);
    // Flickity's `groupCells` equivalent: how many slides one arrow press /
    // one page dot advances. 1 (the default) steps slide by slide.
    this.slidesPerGroup = Math.max(1, Math.min(options.slidesPerGroup || 1, this.slidesPerView));
    // A loop only makes sense when there is something off-screen to loop to.
    this.loop = Boolean(options.loop) && this.realCount > this.slidesPerView;
    this.prevNextButtons = options.prevNextButtons !== false;
    this.pageDots = Boolean(options.pageDots);
    this.adaptiveHeight = Boolean(options.adaptiveHeight);
    this.draggable = options.draggable !== false;
    this.arrowLabels = options.arrowLabels || { previous: 'Previous slide', next: 'Next slide' };
    this.pauseLabels = options.pauseLabels || { pause: 'Pause carousel', play: 'Play carousel' };

    this.selectedIndex = 0;
    this.isJumping = false;
    this.autoPlayTimer = null;
    this.pauseReasons = new Set();
    this.listeners = [];
    this.observers = [];
    this.cleanups = [];
    this.handlers = {};

    this.motionQuery = window.matchMedia(REDUCED_MOTION_QUERY);

    this.build(options.initialIndex || 0);
  }

  build(initialIndex) {
    const { options } = this;

    if (options.role !== false) {
      this.element.setAttribute('role', 'region');
      if (options.ariaLabel && !this.element.getAttribute('aria-label')) {
        this.element.setAttribute('aria-label', options.ariaLabel);
      }
    }

    // aria-live only when nothing rotates on its own, or every autoplay tick
    // is announced to screen readers.
    if (options.ariaLive !== false && !this.autoPlayDelay) {
      this.track.setAttribute('aria-live', 'polite');
    }

    if (!this.draggable) {
      this.viewport.style.overflowX = 'hidden';
      this.viewport.style.touchAction = 'pan-y';
    }

    if (this.loop) this.addClones();
    if (this.prevNextButtons && this.realCount > this.slidesPerView) this.addArrows();
    if (this.pageDots && this.dotCount > 1) this.addDots();
    if (this.autoPlayDelay) this.addPauseButton();

    this.bindScroll();
    this.bindPauseTriggers();
    this.bindResize();

    this.select(initialIndex, { instant: true });
    if (this.adaptiveHeight) this.applyAdaptiveHeight();

    if (this.autoPlayDelay) {
      this.bindAutoPlayGates();
      this.startAutoPlay();
    }

    // imagesLoaded equivalent — late-loading images change slide geometry, so
    // remeasure once each settles rather than pulling in the vendor package.
    this.watchImages();

    this.emit('ready');
  }

  /* ---------------------------------------------------------------- DOM */

  addClones() {
    // Clone a full viewport's worth so multi-slide carousels don't expose a
    // gap at the seam.
    const leadSources = this.realSlides.slice(-this.slidesPerView);
    const tailSources = this.realSlides.slice(0, this.slidesPerView);

    this.leadClones = leadSources.map((slide) => this.makeClone(slide));
    this.tailClones = tailSources.map((slide) => this.makeClone(slide));

    this.leadClones.forEach((clone) => this.track.insertBefore(clone, this.track.firstChild));
    this.tailClones.forEach((clone) => this.track.appendChild(clone));
  }

  /**
   * Clones are decorative duplicates: hidden from assistive tech, untabbable,
   * and stripped of every identifier analytics keys off, so a clone can never
   * double-count an impression or a click.
   */
  makeClone(slide) {
    const clone = slide.cloneNode(true);
    clone.setAttribute('data-clone', '');
    clone.setAttribute('aria-hidden', 'true');

    const strip = [
      'id',
      'data-tracking-id',
      'data-promo-tracking',
      'data-promo-tracking-desktop',
      'data-promo-tracking-mobile',
      'data-promo-type',
      'data-promo-creative',
      'data-announcement',
      'data-announcement-desktop',
      'data-container-type',
    ];
    [clone, ...clone.querySelectorAll('*')].forEach((node) => {
      strip.forEach((attr) => node.removeAttribute(attr));
    });

    clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach((node) => {
      node.setAttribute('tabindex', '-1');
    });

    return clone;
  }

  addArrows() {
    this.prevButton = this.makeArrow('previous');
    this.nextButton = this.makeArrow('next');

    this.on(this.prevButton, 'click', (event) => {
      event.preventDefault();
      this.previous();
    });
    this.on(this.nextButton, 'click', (event) => {
      event.preventDefault();
      this.next();
    });

    this.element.appendChild(this.prevButton);
    this.element.appendChild(this.nextButton);
  }

  makeArrow(direction) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `${this.classes.arrow} ${
      direction === 'previous' ? this.classes.arrowPrev : this.classes.arrowNext
    }`;
    button.setAttribute('aria-label', this.arrowLabels[direction]);

    const arrowShape = this.options.arrowShape || 'M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z';
    const arrowTransform = this.options.arrowShape ? '' : ' transform="translate(15,0)"';
    button.innerHTML = `<svg viewBox="0 0 100 100" aria-hidden="true" focusable="false"><path d="${arrowShape}"${arrowTransform}></path></svg>`;
    return button;
  }

  /**
   * Number of distinct slide positions the carousel can rest at. This is a
   * *slide* index bound, not a dot count — see `dotCount` for grouping.
   * Mirrors the marker formula in `snippets/carousel-navigation.liquid:43-48`.
   */
  get pageCount() {
    if (this.realCount === 0) return 0;
    return Math.max(1, this.realCount - this.slidesPerView + 1);
  }

  /** One dot per group of `slidesPerGroup` slides. */
  get dotCount() {
    if (this.realCount === 0) return 0;
    return Math.max(1, Math.ceil((this.pageCount - 1) / this.slidesPerGroup) + 1);
  }

  get dotIndex() {
    return Math.min(this.dotCount - 1, Math.round(this.selectedIndex / this.slidesPerGroup));
  }

  addDots() {
    const nav = document.createElement('div');
    nav.className = this.classes.dots;

    for (let i = 0; i < this.dotCount; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = this.classes.dot;
      dot.setAttribute('aria-label', `Go to slide ${i * this.slidesPerGroup + 1}`);
      nav.appendChild(dot);
    }

    this.on(nav, 'click', (event) => {
      const dot = event.target.closest(`.${this.classes.dot}`);
      if (!dot) return;
      this.select(Array.from(nav.children).indexOf(dot) * this.slidesPerGroup);
    });

    this.dotsNav = nav;
    this.element.appendChild(nav);
    this.updateDots();
  }

  updateDots() {
    if (!this.dotsNav) return;
    const current = this.dotIndex;
    Array.from(this.dotsNav.children).forEach((dot, i) => {
      const active = i === current;
      dot.classList.toggle(this.classes.dotActive, active);
      if (active) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });
  }

  /**
   * WCAG 2.2 SC 2.2.2 — autoplay speed is merchant-configurable and can exceed
   * 5s, so moving content needs a pause control. Visually hidden until focused.
   */
  addPauseButton() {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = this.classes.pause;
    button.setAttribute('aria-label', this.pauseLabels.pause);
    button.textContent = this.pauseLabels.pause;

    this.on(button, 'click', () => {
      const paused = this.pauseReasons.has('user');
      const label = paused ? this.pauseLabels.pause : this.pauseLabels.play;
      button.setAttribute('aria-label', label);
      button.textContent = label;
      if (paused) {
        this.unpause('user');
      } else {
        this.pause('user');
      }
    });

    this.pauseButton = button;
    this.element.appendChild(button);
  }

  /* ------------------------------------------------------------ geometry */

  get slideWidth() {
    // Measure a real slide — with clones in place the first child is a clone,
    // and clones can differ in width once responsive text wraps.
    const reference = this.realSlides[0] || this.track.firstElementChild;
    if (!reference) return this.viewport.clientWidth;
    const width = reference.offsetWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return width + gap;
  }

  get leadOffset() {
    return this.loop ? this.slidesPerView : 0;
  }

  offsetFor(rawIndex) {
    return rawIndex * this.slideWidth;
  }

  get rawIndex() {
    const width = this.slideWidth;
    if (!width) return this.leadOffset;
    return Math.round(this.viewport.scrollLeft / width);
  }

  toRealIndex(rawIndex) {
    if (!this.loop) return Math.max(0, Math.min(rawIndex, this.pageCount - 1));
    const shifted = rawIndex - this.leadOffset;
    return ((shifted % this.realCount) + this.realCount) % this.realCount;
  }

  toRawIndex(realIndex) {
    return realIndex + this.leadOffset;
  }

  /**
   * Assign scrollLeft directly rather than scrollTo({behavior:'instant'}) —
   * uneven support, and 'smooth' would reveal the seam.
   *
   * `scroll-snap-type: x mandatory` fights programmatic scrollLeft writes and
   * the order below matters. Without the forced reflow between the write and
   * the restore, Safari coalesces both style changes and re-snaps to the
   * pre-jump slide, presenting as the carousel freezing at the boundary.
   * Validated on-device; do not "simplify" this.
   */
  jumpTo(left) {
    this.isJumping = true;
    const { viewport } = this;
    const snap = getComputedStyle(viewport).scrollSnapType;

    viewport.style.scrollSnapType = 'none';
    viewport.scrollLeft = left;
    void viewport.offsetWidth; // force reflow BEFORE restoring
    viewport.style.scrollSnapType = snap && snap !== 'none' ? snap : 'x mandatory';

    window.clearTimeout(this.jumpTimer);
    this.jumpTimer = window.setTimeout(() => {
      this.isJumping = false;
    }, SETTLE_MS + 30);
  }

  /**
   * Flickity sized its container to the tallest *visible* cell. With
   * `slidesPerView > 1` more than one slide is on screen, so measuring only
   * the active one would clip the taller neighbours.
   */
  applyAdaptiveHeight() {
    if (!this.adaptiveHeight) return;

    const visible = this.realSlides.slice(this.selectedIndex, this.selectedIndex + this.slidesPerView);
    if (visible.length === 0) return;

    const height = visible.reduce((tallest, slide) => Math.max(tallest, slide.offsetHeight), 0);
    if (height > 0) this.viewport.style.height = `${height}px`;
  }

  /** Flickity API parity — consumers call this after layout changes. */
  resize() {
    if (!this.valid) return;
    this.applyAdaptiveHeight();
    this.updateNavigationState();
    // Re-anchor: slide widths just changed, so the old scrollLeft is stale.
    this.jumpTo(this.offsetFor(this.toRawIndex(this.selectedIndex)));
  }

  watchImages() {
    const images = Array.from(this.track.querySelectorAll('img')).filter((img) => !img.complete);
    if (images.length === 0) return;

    const onLoad = debounce(() => this.resize(), 100);
    images.forEach((img) => {
      this.on(img, 'load', onLoad);
      this.on(img, 'error', onLoad);
    });
  }

  /* -------------------------------------------------------------- scroll */

  bindScroll() {
    this.onScrollSettled = debounce(() => {
      if (this.isJumping) return;
      this.handleSettle();
    }, SETTLE_MS);

    this.on(
      this.viewport,
      'scroll',
      () => {
        // A user-initiated scroll is in flight — don't let the timer fight a swipe.
        if (!this.isJumping) this.pause('scrolling');
        this.onScrollSettled();
      },
      { passive: true },
    );
  }

  handleSettle() {
    const raw = this.rawIndex;

    if (this.loop) {
      // Landed on a lead clone (copy of the tail) — jump to the real tail.
      if (raw < this.leadOffset) {
        const realIndex = this.realCount - (this.leadOffset - raw);
        this.jumpTo(this.offsetFor(this.toRawIndex(realIndex)));
        this.setSelected(realIndex);
        this.resumeAutoPlay();
        return;
      }
      // Landed on a tail clone (copy of the head) — jump to the real head.
      if (raw >= this.leadOffset + this.realCount) {
        const realIndex = raw - (this.leadOffset + this.realCount);
        this.jumpTo(this.offsetFor(this.toRawIndex(realIndex)));
        this.setSelected(realIndex);
        this.resumeAutoPlay();
        return;
      }
    }

    this.setSelected(this.toRealIndex(raw));
    this.resumeAutoPlay();
  }

  setSelected(realIndex) {
    const changed = realIndex !== this.selectedIndex;
    this.selectedIndex = realIndex;

    this.realSlides.forEach((slide, i) => {
      slide.classList.toggle(this.classes.selected, i === realIndex);
    });

    this.updateDots();
    this.updateNavigationState();
    if (this.adaptiveHeight) this.applyAdaptiveHeight();
    if (changed) this.emit('change', realIndex);
  }

  /** In loop mode arrows must never disable, or the wrap becomes unreachable. */
  updateNavigationState() {
    if (this.loop || !this.prevButton || !this.nextButton) return;

    const atStart = this.selectedIndex <= 0;
    const atEnd = this.selectedIndex >= this.pageCount - 1;

    this.prevButton.classList.toggle(this.classes.arrowDisabled, atStart);
    this.prevButton.toggleAttribute('disabled', atStart);
    this.nextButton.classList.toggle(this.classes.arrowDisabled, atEnd);
    this.nextButton.toggleAttribute('disabled', atEnd);
  }

  /* ----------------------------------------------------------- public API */

  select(realIndex, { instant = false } = {}) {
    if (this.realCount === 0) return;

    const bounded = this.loop
      ? ((realIndex % this.realCount) + this.realCount) % this.realCount
      : Math.max(0, Math.min(realIndex, this.pageCount - 1));
    const left = this.offsetFor(this.toRawIndex(bounded));

    if (instant) {
      this.jumpTo(left);
    } else {
      this.viewport.scrollTo({ left, behavior: 'smooth' });
    }

    this.setSelected(bounded);
  }

  next() {
    this.step(1);
  }

  previous() {
    this.step(-1);
  }

  /**
   * Step by raw offset rather than real index so a move across the seam
   * scrolls onto a clone — that is what makes the wrap look continuous
   * instead of sweeping backward across every slide.
   */
  step(direction) {
    const distance = direction * this.slidesPerGroup;

    if (!this.loop) {
      this.select(this.selectedIndex + distance);
      return;
    }

    const targetRaw = this.toRawIndex(this.selectedIndex) + distance;
    this.viewport.scrollTo({ left: this.offsetFor(targetRaw), behavior: 'smooth' });
    this.setSelected(this.toRealIndex(targetRaw));
  }

  /* -------------------------------------------------------------- events */

  on_(name, handler) {
    (this.handlers[name] = this.handlers[name] || []).push(handler);
  }

  emit(name, payload) {
    (this.handlers[name] || []).forEach((handler) => handler(payload));
    const consumer = this.options.on && this.options.on[name];
    if (consumer) consumer(payload);
  }

  /* ------------------------------------------------------------ autoplay */

  get canAutoPlay() {
    return Boolean(this.autoPlayDelay) && !this.motionQuery.matches && this.pauseReasons.size === 0;
  }

  startAutoPlay() {
    this.stopTimer();
    if (!this.canAutoPlay) return;

    this.autoPlayTimer = window.setInterval(() => {
      if (!this.canAutoPlay) {
        this.stopTimer();
        return;
      }
      this.next();
    }, this.autoPlayDelay);
  }

  stopTimer() {
    if (this.autoPlayTimer) {
      window.clearInterval(this.autoPlayTimer);
      this.autoPlayTimer = null;
    }
  }

  pause(reason = 'user') {
    this.pauseReasons.add(reason);
    this.stopTimer();
  }

  unpause(reason = 'user') {
    this.pauseReasons.delete(reason);
    this.resumeAutoPlay();
  }

  resumeAutoPlay() {
    this.pauseReasons.delete('scrolling');
    if (this.canAutoPlay) this.startAutoPlay();
  }

  // Flickity API parity for the theme editors.
  pausePlayer() {
    this.pause('editor');
  }

  unpausePlayer() {
    this.unpause('editor');
  }

  bindPauseTriggers() {
    if (!this.autoPlayDelay) return;

    this.on(this.element, 'mouseenter', () => this.pause('hover'));
    this.on(this.element, 'mouseleave', () => this.unpause('hover'));
    this.on(this.element, 'focusin', () => this.pause('focus'));
    this.on(this.element, 'focusout', () => this.unpause('focus'));

    // Touch dominates here (73% mobile) — Flickity paused on drag, so match it.
    this.on(this.element, 'touchstart', () => this.pause('touch'), { passive: true });
    this.on(this.element, 'touchend', () => this.unpause('touch'), { passive: true });
    this.on(this.element, 'touchcancel', () => this.unpause('touch'), { passive: true });
    this.on(this.element, 'pointerdown', () => this.pause('touch'));
    this.on(this.element, 'pointerup', () => this.unpause('touch'));
    this.on(this.element, 'pointercancel', () => this.unpause('touch'));
  }

  bindAutoPlayGates() {
    // Listen for change — reading the query once misses a mid-session toggle.
    const onMotionChange = () => {
      if (this.motionQuery.matches) {
        this.stopTimer();
      } else {
        this.resumeAutoPlay();
      }
    };
    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener('change', onMotionChange);
      this.cleanups.push(() => this.motionQuery.removeEventListener('change', onMotionChange));
    }

    // Without this, a throttled background timer queues a burst of scrolls
    // that all fire the moment the tab is refocused.
    this.on(document, 'visibilitychange', () => {
      if (document.hidden) {
        this.pause('hidden');
      } else {
        this.unpause('hidden');
      }
    });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.unpause('offscreen');
            } else {
              this.pause('offscreen');
            }
          });
        },
        { threshold: 0 },
      );
      observer.observe(this.element);
      this.observers.push(observer);
    }
  }

  bindResize() {
    if (!('ResizeObserver' in window)) return;
    // Slide widths are percentage-based, so a container resize invalidates
    // both the adaptive height and the current scroll anchor.
    const observer = new ResizeObserver(
      debounce(() => {
        if (!this.valid) return;
        this.applyAdaptiveHeight();
        this.jumpTo(this.offsetFor(this.toRawIndex(this.selectedIndex)));
      }, 150),
    );
    observer.observe(this.element);
    this.observers.push(observer);
  }

  /* ------------------------------------------------------------ teardown */

  on(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    this.listeners.push({ target, type, handler, options });
  }

  destroy() {
    if (!this.valid) return;

    this.stopTimer();
    window.clearTimeout(this.jumpTimer);

    this.listeners.forEach(({ target, type, handler, options }) => {
      target.removeEventListener(type, handler, options);
    });
    this.listeners = [];

    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];

    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];

    this.handlers = {};

    // Remove everything we injected so repeated theme-editor re-inits cannot
    // stack clones, arrows, or dots.
    Array.from(this.track.children).forEach((child) => {
      if (child.hasAttribute('data-clone')) child.remove();
    });
    this.element
      .querySelectorAll(
        `:scope > .${this.classes.arrow}, :scope > .${this.classes.dots}, :scope > .${this.classes.pause}`,
      )
      .forEach((node) => node.remove());

    this.realSlides.forEach((slide) => slide.classList.remove(this.classes.selected));
    this.track.removeAttribute('aria-live');
    this.viewport.style.scrollSnapType = '';
    this.viewport.style.height = '';
  }
}

export default ScrollCarousel;
